import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import HeroShowcase from "../components/home/HeroShowcase.jsx";
import Marquee from "../components/ui/Marquee.jsx";
import SectionHeading from "../components/ui/SectionHeading.jsx";
import CTABanner from "../components/ui/CTABanner.jsx";
import ClubCard from "../components/cards/ClubCard.jsx";
import FacilityCard from "../components/cards/FacilityCard.jsx";
import TestimonialCard from "../components/cards/TestimonialCard.jsx";
import PreSaleBanner from "../components/PreSaleBanner.jsx";
import { Reveal, MaskLines, ImageReveal } from "../components/motion/Reveal.jsx";
import useSeo from "../hooks/useSeo.js";
import { CLUBS, FACILITIES, TESTIMONIALS, MARQUEE_WORDS, ENRICHED } from "../content.js";

function WhoWeAre() {
  return (
    <section className="mx-auto max-w-[1440px] px-6 py-24 lg:px-12 lg:py-32">
      <div className="grid items-center gap-14 lg:grid-cols-12 lg:gap-20">
        <div className="lg:col-span-5">
          <SectionHeading
            eyebrow="Who We Are"
            lines={["More Than", "A Gym."]}
            copy="Oneiros Sports Club, founded in 2019, was built with a singular purpose — to redefine what a sports club can truly offer. A world-class ecosystem for athletes, swimmers, fitness enthusiasts and future sports champions, across Surat."
          />
          <Reveal delay={0.2}>
            <Link
              data-testid="home-our-story-link"
              to="/about"
              className="group mt-8 inline-flex items-center gap-3 border-b-2 border-brand pb-2 text-xs font-bold uppercase tracking-[0.2em] text-navy transition-colors hover:text-brand"
            >
              Our Story
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
        <div className="grid gap-6 lg:col-span-7 lg:grid-cols-12">
          <ImageReveal src="/oneiros/kat-lifestyle.jpg" alt="Inside Oneiros Sports Club, Surat" className="lg:col-span-7 aspect-[4/5] lg:aspect-[3/4]" />
          <div className="flex flex-col gap-6 lg:col-span-5">
            <ImageReveal src="/oneiros/european-studio.jpg" alt="Oneiros European style fitness studio" className="aspect-square lg:aspect-[4/5]" delay={0.15} />
            <Reveal delay={0.25} className="border border-hairline bg-mist p-8">
              <p className="font-display text-2xl leading-snug text-navy">
                One brand. Multiple destinations. <span className="text-brand">One stronger future.</span>
              </p>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

function Destinations() {
  return (
    <section className="mx-auto max-w-[1440px] px-6 py-24 lg:px-12 lg:py-32">
      <div className="flex flex-col justify-between gap-8 md:flex-row md:items-end">
        <SectionHeading
          eyebrow="Our Destinations"
          lines={["One Brand.", "Three Destinations."]}
        />
        <Reveal delay={0.2}>
          <Link data-testid="home-all-clubs-link" to="/our-clubs" className="group inline-flex items-center gap-2 border-b-2 border-brand pb-2 text-xs font-bold uppercase tracking-[0.2em] text-navy hover:text-brand">
            View All Clubs <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </Reveal>
      </div>
      <div className="mt-14 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
        {CLUBS.map((club, i) => (
          <ClubCard key={club.id} club={club} testId={`home-club-card-${club.id}`} />
        ))}
      </div>
    </section>
  );
}

function BlueBand() {
  return (
    <section data-testid="home-blue-band" className="grain relative overflow-hidden bg-royal text-white">
      <div className="mx-auto grid max-w-[1440px] items-center gap-0 px-0 lg:grid-cols-2">
        <div className="px-6 py-20 lg:px-12 lg:py-28">
          <Reveal>
            <p className="eyebrow flex items-center gap-3">
              <span className="inline-block h-[2px] w-8 bg-brand" /> Blue Membership
            </p>
          </Reveal>
          <MaskLines lines={["Gift Your Family", "12–25 Years of", "Fitness & Wellness."]} className="mt-6 font-display text-4xl leading-[1.05] sm:text-5xl lg:text-6xl" />
          <Reveal delay={0.2}>
            <p className="mt-6 max-w-md text-base leading-relaxed text-white/70 md:text-lg">
              A membership designed for families — more time together, more active years, more of what matters. 12 years at Katargam, 15 at Adajan, 25 at Varachha.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <Link
              data-testid="home-blue-membership-cta"
              to="/blue-membership"
              className="group mt-9 inline-flex items-center gap-3 rounded-full bg-brand px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-white transition-colors hover:bg-brand-dark"
            >
              Explore Blue Membership
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
        <ImageReveal src="/oneiros/family-workout.png" alt="Oneiros Blue Membership — family of four training in Oneiros tees" className="h-full min-h-[320px] w-full lg:min-h-[520px]" imgClassName="opacity-90" />
      </div>
    </section>
  );
}

function FacilitiesPreview() {
  const featured = [FACILITIES[0], FACILITIES[1], FACILITIES[5], FACILITIES[3], FACILITIES[12], FACILITIES[4], FACILITIES[13], FACILITIES[15]];
  return (
    <section className="mx-auto max-w-[1440px] px-6 py-24 lg:px-12 lg:py-32">
      <div className="flex flex-col justify-between gap-8 md:flex-row md:items-end">
        <SectionHeading eyebrow="World-Class Facilities" lines={["Everything You Need.", "Under One Roof."]} />
        <Reveal delay={0.2}>
          <Link data-testid="home-all-facilities-link" to="/facilities" className="group inline-flex items-center gap-2 border-b-2 border-brand pb-2 text-xs font-bold uppercase tracking-[0.2em] text-navy hover:text-brand">
            View All Facilities <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </Reveal>
      </div>
      <div className="mt-14 grid grid-cols-2 gap-4 md:grid-cols-3 md:gap-6 lg:grid-cols-6">
        {featured.map((f) => (
          <FacilityCard key={f.id} facility={f} index={FACILITIES.indexOf(f)} />
        ))}
      </div>
    </section>
  );
}

function Mission5L() {
  return (
    <section data-testid="home-5l-section" className="mx-auto max-w-[1440px] px-6 py-24 lg:px-12 lg:py-32">
      <div className="grid items-center gap-14 lg:grid-cols-12 lg:gap-20">
        <div className="lg:col-span-6">
          <Reveal>
            <p className="eyebrow flex items-center gap-3">
              <span className="inline-block h-[2px] w-8 bg-brand" /> The 5 Lakh Kgs Revolution
            </p>
          </Reveal>
          <MaskLines lines={["5L Kgs.", "One City.", "One Goal."]} className="mt-6 font-display text-5xl leading-[1.02] text-navy sm:text-6xl lg:text-7xl" />
          <Reveal delay={0.15}>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-mute md:text-lg">{ENRICHED.mission5l}</p>
          </Reveal>
          <Reveal delay={0.25}>
            <Link
              data-testid="home-5l-cta"
              to="/community"
              className="group mt-9 inline-flex items-center gap-3 rounded-full bg-navy px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-navy-obsidian"
            >
              Be Part of the Movement
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
        <div className="relative lg:col-span-6">
          <ImageReveal src="/oneiros/family-workout-2.png" alt="A family of four training together at Oneiros Sports Club" className="aspect-[4/5] w-full" />
          <Reveal delay={0.3} className="absolute -bottom-8 -left-2 border border-hairline bg-white p-7 shadow-[0_24px_60px_-30px_rgba(11,25,44,0.35)] md:left-8">
            <p className="font-display text-5xl text-navy">5,00,000<span className="text-brand">kg</span></p>
            <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.25em] text-mute">Half a million kilos. Zero excuses.</p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function BuiltToPerform() {
  const points = ["Asia's Strongest Gym", "300 Kg Lift Story", "International-Standard Equipment", "Professional Guidance"];
  return (
    <section className="grain relative overflow-hidden bg-navy-obsidian py-24 text-white lg:py-32">
      <div className="mx-auto grid max-w-[1440px] items-center gap-14 px-6 lg:grid-cols-2 lg:gap-20 lg:px-12">
        <div className="order-2 lg:order-1">
          <Reveal>
            <p className="eyebrow flex items-center gap-3">
              <span className="inline-block h-[2px] w-8 bg-brand" /> Built to Perform
            </p>
          </Reveal>
          <MaskLines lines={["World-Class", "Strength &", "Conditioning."]} className="mt-6 font-display text-4xl leading-[1.05] sm:text-5xl lg:text-6xl" />
          <Reveal delay={0.15}>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-white/70 md:text-lg">
              India's Strongest Gym. Built for those who train to dominate — a platform for athletes, swimmers and sports champions, setting the highest benchmark of service in the industry.
            </p>
          </Reveal>
          <ul className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {points.map((p, i) => (
              <Reveal key={p} delay={0.1 + i * 0.08}>
                <li className="flex items-center gap-4 border border-white/12 p-4">
                  <span className="font-display text-xl text-brand">{String(i + 1).padStart(2, "0")}</span>
                  <span className="text-sm font-semibold uppercase tracking-[0.12em] text-white/85">{p}</span>
                </li>
              </Reveal>
            ))}
          </ul>
        </div>
        <div className="order-1 lg:order-2">
          <ImageReveal src="/oneiros/strongest-gym.jpg" alt="Asia's Strongest Gym at Oneiros" className="aspect-[4/5] w-full md:aspect-[5/5]" />
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  return (
    <section className="py-24 lg:py-32">
      <div className="mx-auto max-w-[1440px] px-6 lg:px-12">
        <SectionHeading eyebrow="Member Voices" lines={["Real People.", "Real Transformations."]} />
      </div>
      <div className="no-scrollbar mt-14 flex snap-x snap-mandatory gap-6 overflow-x-auto px-6 pb-4 lg:px-12" data-testid="home-testimonials">
        {TESTIMONIALS.map((t) => (
          <div key={t.id} className="w-[85vw] shrink-0 snap-start sm:w-[55vw] lg:w-[calc((100%-3rem)/2)] xl:w-[calc((100%-6rem)/3)]">
            <TestimonialCard testimonial={t} />
          </div>
        ))}
      </div>
    </section>
  );
}

export default function Home() {
  useSeo({
    title: "Oneiros Sports Club Surat | Premium Sports, Fitness & Wellness Club",
    description:
      "Oneiros Sports Club — more than a gym. A complete sports, fitness, wellness, family and community ecosystem in Surat with destinations in Katargam, Adajan & Varachha.",
    image: "/oneiros/adajan-hero.jpg",
  });
  return (
    <>
      <HeroShowcase />
      <Marquee items={MARQUEE_WORDS} />
      <WhoWeAre />
      <Destinations />
      <PreSaleBanner />
      <BlueBand />
      <FacilitiesPreview />
      <Mission5L />
      <BuiltToPerform />
      <Testimonials />
      <CTABanner
        eyebrow="Begin Today"
        lines={["A Stronger Tomorrow", "Starts Today."]}
        primary={{ label: "Enquire Now", to: "/contact" }}
        secondary={{ label: "Visit a Club", to: "/our-clubs" }}
        testId="home-final-cta"
      />
    </>
  );
}
